import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { MapPin, CalendarDays, Users, Search, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function Segment({ segRef, Icon, label, value, onClick, testId, isLast }) {
  return (
    <>
      <Box
        ref={segRef}
        component="button" type="button" onClick={onClick} data-testid={testId}
        sx={{
          flex: { xs: "0 0 auto", sm: 1 }, width: { xs: "100%", sm: "auto" }, minWidth: 0, textAlign: "left", border: 0, cursor: "pointer", bgcolor: "transparent",
          px: { xs: 1.5, md: 2.25 }, py: { xs: 1.25, sm: 1.1 }, borderRadius: { xs: 2.5, sm: 999 }, font: "inherit", color: "inherit",
          display: "flex", alignItems: "center", gap: 1,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Icon size={15} className="text-gold" style={{ flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 10.5, fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>{label}</Box>
          <Box sx={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</Box>
        </Box>
      </Box>
      {!isLast && <Box sx={{ width: { xs: "100%", sm: "1px" }, height: { xs: "1px", sm: 26 }, bgcolor: "divider", flexShrink: 0 }} />}
    </>
  );
}

export default function HeroSearchBar() {
  const nav = useNavigate();
  const [places, setPlaces] = useState(null); // null = still loading
  const [loadError, setLoadError] = useState(false);
  const [destination, setDestination] = useState("");
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(0);
  const [openSegment, setOpenSegment] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const whereRef = useRef(null);
  const checkinRef = useRef(null);
  const checkoutRef = useRef(null);
  const guestsRef = useRef(null);
  const segmentRefs = { where: whereRef, checkin: checkinRef, checkout: checkoutRef, guests: guestsRef };

  useEffect(() => {
    Promise.all([
      api.get("/villas/destinations"),
      api.get("/villas?limit=200"),
    ]).then(([destRes, villasRes]) => {
      const regions = destRes.data.map((d) => ({ label: d.name, sub: `${d.count}+ villas`, kind: "region" }));
      const seen = new Map();
      villasRes.data.forEach((v) => {
        if (v.location && !seen.has(v.location)) seen.set(v.location, v.destination_region);
      });
      const cities = [...seen.entries()].map(([location, region]) => ({ label: location, sub: region, kind: "place" }));
      setPlaces([...regions, ...cities]);
    }).catch(() => { setPlaces([]); setLoadError(true); });
  }, []);

  const goToSegment = (segment) => { setAnchorEl(segmentRefs[segment].current); setOpenSegment(segment); };
  const openPopover = (segment) => () => goToSegment(segment);
  const close = () => setOpenSegment(null);

  const query = destination.trim().toLowerCase();
  const filteredPlaces = places === null ? null
    : query ? places.filter((p) => p.label.toLowerCase().includes(query))
    : places.filter((p) => p.kind === "region");

  const runSearch = (dest) => {
    const d = (dest ?? destination).trim();
    if (!d) { toast("Please add a destination to search"); goToSegment("where"); return; }
    const params = new URLSearchParams();
    params.set("destination", d);
    if (guests > 0) params.set("guests", guests);
    if (checkIn) params.set("from", checkIn.toDate().toISOString());
    if (checkOut) params.set("to", checkOut.toDate().toISOString());
    close();
    nav(`/villas?${params.toString()}`);
  };
  const onSearch = () => runSearch();

  const popoverProps = {
    anchorEl, onClose: close,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
    transformOrigin: { vertical: "top", horizontal: "center" },
    slotProps: { paper: { sx: { mt: 1, borderRadius: 3 } } },
  };

  return (
    <Box
      sx={{
        display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" },
        bgcolor: "background.paper", borderRadius: { xs: 3.5, sm: 999 }, boxShadow: "0 20px 50px -15px rgba(0,0,0,0.5)",
        p: { xs: 1, sm: 0.5 }, width: "100%", maxWidth: 760, mx: "auto",
      }}
      data-testid="hero-search-bar"
    >
      <Segment segRef={whereRef} Icon={MapPin} label="Where" value={destination || "Anywhere"} onClick={openPopover("where")} testId="hero-search-where" />
      <Segment segRef={checkinRef} Icon={CalendarDays} label="Check in" value={checkIn ? checkIn.format("DD MMM") : "Add dates"} onClick={openPopover("checkin")} testId="hero-search-checkin" />
      <Segment segRef={checkoutRef} Icon={CalendarDays} label="Check out" value={checkOut ? checkOut.format("DD MMM") : "Add dates"} onClick={openPopover("checkout")} testId="hero-search-checkout" />
      <Segment segRef={guestsRef} Icon={Users} label="Guests" value={guests > 0 ? `${guests} guest${guests > 1 ? "s" : ""}` : "Add guests"} onClick={openPopover("guests")} testId="hero-search-guests" isLast />
      <IconButton
        onClick={onSearch} data-testid="hero-search-btn"
        sx={{ display: { xs: "none", sm: "inline-flex" }, bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.main" }, width: 44, height: 44, flexShrink: 0, ml: 0.5 }}
      >
        <Search size={17} />
      </IconButton>
      <Button
        onClick={onSearch} data-testid="hero-search-btn-mobile"
        variant="contained" disableElevation fullWidth
        startIcon={<Search size={16} />}
        sx={{ display: { xs: "flex", sm: "none" }, mt: 1, py: 1.2, borderRadius: 2.5, textTransform: "none", fontWeight: 600, fontSize: 14, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
      >
        Search
      </Button>

      <Popover open={openSegment === "where"} {...popoverProps} slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, width: 320 } } }}>
        <Box sx={{ p: 2.5 }}>
          <TextField
            autoFocus fullWidth size="small" placeholder="Search by city or region…"
            value={destination} onChange={(e) => setDestination(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
            inputProps={{ "data-testid": "hero-where-input" }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10.5 }}>
            {query ? "Matching places" : "Popular destinations"}
          </Typography>
          <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
            {places === null ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>Loading destinations…</Typography>
            ) : filteredPlaces.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                {loadError ? "Couldn't load suggestions — you can still type a destination and search." : "No matches. Try a different city or region."}
              </Typography>
            ) : filteredPlaces.map((p) => (
              <Box
                key={p.label} component="button" type="button"
                onClick={() => { setDestination(p.label); goToSegment("checkin"); }}
                data-testid={`hero-where-option-${p.label}`}
                sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%", textAlign: "left", border: 0, bgcolor: "transparent", px: 1, py: 1, borderRadius: 2, cursor: "pointer", font: "inherit", color: "inherit", "&:hover": { bgcolor: "action.hover" } }}
              >
                <MapPin size={14} className={p.kind === "region" ? "text-gold" : undefined} style={{ flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: p.kind === "region" ? 600 : 400 }} noWrap>{p.label}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{p.sub}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
          {destination.trim() && (
            <Button fullWidth variant="text" size="small" onClick={() => runSearch()} data-testid="hero-where-search-anyway" sx={{ mt: 1, justifyContent: "flex-start", textTransform: "none", fontSize: 13 }}>
              Search "{destination.trim()}"
            </Button>
          )}
        </Box>
      </Popover>

      <Popover open={openSegment === "checkin"} {...popoverProps}>
        <DateCalendar value={checkIn} onChange={(d) => { setCheckIn(d); goToSegment("checkout"); }} disablePast />
      </Popover>

      <Popover open={openSegment === "checkout"} {...popoverProps}>
        <DateCalendar value={checkOut} onChange={(d) => { setCheckOut(d); goToSegment("guests"); }} minDate={checkIn || dayjs()} />
      </Popover>

      <Popover open={openSegment === "guests"} {...popoverProps} slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, width: 220 } } }}>
        <Box sx={{ p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Guests</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton size="small" onClick={() => setGuests((g) => Math.max(0, g - 1))} disabled={guests <= 0} data-testid="hero-guests-minus" sx={{ border: 1, borderColor: "divider", width: 28, height: 28 }}><Minus size={13} /></IconButton>
            <Typography sx={{ width: 18, textAlign: "center" }} data-testid="hero-guests-value">{guests}</Typography>
            <IconButton size="small" onClick={() => setGuests((g) => g + 1)} data-testid="hero-guests-plus" sx={{ border: 1, borderColor: "divider", width: 28, height: 28 }}><Plus size={13} /></IconButton>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
